import {Box, HStack, Image, Text, VStack} from '@chakra-ui/react';
import {useHalState} from '@synako/haligator';
import {ReactNode} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useClient} from '@/haligator-factory';
import {LogoFranceFrais} from '@/components/svg/LogoFranceFrais';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';

/**
 * Accueil : Photo d'ambiance du bloc d'accueil, choisie par le serveur selon le métier ou la société du
 * client (`url_image_ambiance` de get-client). Version ordinateur : à droite de la carte
 * d'accueil, avec le slogan et le cachet France Frais. Version mobile : en tête du bloc, avec la
 * pastille client et la salutation posées dessus.
 */

// 535 px en version ordinateur et 343 px en mobile, doublés ou triplés sur un écran haute
// densité : une seule largeur sert les deux versions, donc une seule requête
const LARGEUR_IMAGE = 1100;

export const AccueilImageAmbiance: React.FC<{
  variante: 'ordinateur' | 'mobile';
  children?: ReactNode;
}> = ({variante, children}) => {
  const {t} = useTranslation();
  const {data} = useHalState(useClient());
  const url = data?.url_image_ambiance;

  // Pas d'URL tant que le client n'est pas chargé : la version ordinateur attend la photo,
  // la version mobile garde un fond uni sous la salutation
  if (!url && variante === 'ordinateur') {
    return null;
  }

  return (
    <Box
      position='relative'
      overflow='hidden'
      borderRadius='12px'
      bg='blue.navy'
      {...(variante === 'ordinateur'
        ? {flex: '535', minW: 0, minH: '280px'}
        : {h: '221px'})}
    >
      {url && (
        <Image
          position='absolute'
          inset={0}
          width='100%'
          height='100%'
          objectFit='cover'
          src={imageOptimisee(url, LARGEUR_IMAGE, {sans_agrandir: true})}
          alt={t('accueil.image_ambiance.alt')}
        />
      )}
      <Box
        aria-hidden
        position='absolute'
        insetX={0}
        bottom={0}
        h='60%'
        bgGradient='linear(to-t, black.transparency_88, black.transparency_40 40%, transparent)'
      />
      {variante === 'ordinateur' ? (
        <HStack
          position='absolute'
          right='20px'
          bottom='24px'
          spacing='12px'
          align='center'
        >
          <Text
            fontSize='sm'
            color='white'
            fontWeight='600'
            lineHeight='1.3'
            textAlign='right'
          >
            <Trans
              t={t}
              i18nKey='branding.slogan'
              components={{br: <br />}}
            />
          </Text>
          <LogoFranceFrais
            width='84px'
            height='auto'
            color='white'
          />
        </HStack>
      ) : (
        <VStack
          position='absolute'
          insetX={0}
          bottom={0}
          px='20px'
          pb='22px'
          spacing='10px'
          align='start'
        >
          {children}
        </VStack>
      )}
    </Box>
  );
};
